import { useState } from "react";
import axios from "axios";
import {
  Bot, Send, Loader2, Sparkles, MessageSquare,
  Inbox, Star, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MailAgent() {
  const [activeTab, setActiveTab] = useState("priority");
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [priorityEmails, setPriorityEmails] = useState([]);
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [summaryEmailId, setSummaryEmailId] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [composeContext, setComposeContext] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [composeLoading, setComposeLoading] = useState(false);
  const [taskEmailId, setTaskEmailId] = useState("");
  const [extractedTasks, setExtractedTasks] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);

  const fetchPriorityInbox = async () => {
    setPriorityLoading(true);
    try {
      const res = await axios.post(`${API}/mail/agent/priority-inbox?limit=20`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      setPriorityEmails(res.data.emails || []);
    } catch {
      toast.error("Failed to load priority inbox");
    }
    setPriorityLoading(false);
  };

  const handleQuery = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mail/agent/query`, { query }, {
        headers: getHeaders(), withCredentials: true,
      });
      setResponse(res.data.response);
    } catch {
      toast.error("Agent query failed");
    }
    setLoading(false);
  };

  const handleSummarize = async () => {
    if (!summaryEmailId.trim()) return;
    setSummaryLoading(true);
    try {
      const res = await axios.post(`${API}/mail/agent/summarize?email_id=${summaryEmailId.trim()}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      setSummary(res.data.summary);
    } catch {
      toast.error("Failed to summarize");
    }
    setSummaryLoading(false);
  };

  const handleComposeSuggest = async () => {
    setComposeLoading(true);
    try {
      const res = await axios.post(`${API}/mail/agent/compose-suggest?context=${encodeURIComponent(composeContext)}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      setSuggestion(res.data.suggestion);
    } catch {
      toast.error("Failed to get suggestion");
    }
    setComposeLoading(false);
  };

  const handleExtractTasks = async () => {
    if (!taskEmailId.trim()) return;
    setTaskLoading(true);
    try {
      const res = await axios.post(`${API}/mail/agent/extract-tasks?email_id=${taskEmailId.trim()}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      setExtractedTasks(res.data.tasks || []);
    } catch {
      toast.error("Failed to extract tasks");
    }
    setTaskLoading(false);
  };

  return (
    <div className="p-4 max-w-4xl">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Agent Mail</h2>
          <p className="text-[10px] text-slate-400">AI-powered email assistant</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none border border-slate-200 bg-white mb-4">
          <TabsTrigger value="priority" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Star className="w-3.5 h-3.5 mr-1" /> Priority Inbox
          </TabsTrigger>
          <TabsTrigger value="compose" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Smart Compose
          </TabsTrigger>
          <TabsTrigger value="summarize" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Summarize
          </TabsTrigger>
          <TabsTrigger value="tasks" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Zap className="w-3.5 h-3.5 mr-1" /> Extract Tasks
          </TabsTrigger>
          <TabsTrigger value="chat" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Bot className="w-3.5 h-3.5 mr-1" /> Ask Agent
          </TabsTrigger>
        </TabsList>

        <TabsContent value="priority">
          <div className="mb-3">
            <Button size="sm" onClick={fetchPriorityInbox} disabled={priorityLoading}
              className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5">
              {priorityLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
              Analyze Priority Inbox
            </Button>
          </div>
          {priorityEmails.length > 0 && (
            <div className="bg-white border border-slate-200 divide-y divide-slate-100">
              {priorityEmails.map((email) => (
                <div key={email.id} className="px-3 py-2 flex items-center gap-2 hover:bg-app">
                  <Inbox className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-medium text-slate-800 w-28 truncate">
                    {email.from?.name || email.from?.email}
                  </span>
                  <span className="text-xs text-slate-600 flex-1 truncate">{email.subject}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="compose">
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <label className="text-xs font-medium text-slate-600">Describe what you want to write:</label>
            <textarea
              value={composeContext}
              onChange={(e) => setComposeContext(e.target.value)}
              rows={3}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded focus:outline-none focus:border-violet-400"
              placeholder="e.g., A follow-up email to John about the Q2 report deadline..."
            />
            <Button size="sm" onClick={handleComposeSuggest} disabled={composeLoading}
              className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5">
              {composeLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Generate Suggestion
            </Button>
            {suggestion && (
              <div className="p-3 bg-app border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
                {suggestion}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="summarize">
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <label className="text-xs font-medium text-slate-600">Email ID to summarize:</label>
            <Input value={summaryEmailId} onChange={(e) => setSummaryEmailId(e.target.value)}
              placeholder="mail_..." className="h-8 text-xs rounded-none max-w-xs" />
            <Button size="sm" onClick={handleSummarize} disabled={summaryLoading}
              className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5">
              {summaryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
              Summarize
            </Button>
            {summary && (
              <div className="p-3 bg-app border border-slate-200 text-sm text-slate-700">
                {summary}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <label className="text-xs font-medium text-slate-600">Email ID to extract tasks from:</label>
            <Input value={taskEmailId} onChange={(e) => setTaskEmailId(e.target.value)}
              placeholder="mail_..." className="h-8 text-xs rounded-none max-w-xs" />
            <Button size="sm" onClick={handleExtractTasks} disabled={taskLoading}
              className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5">
              {taskLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              Extract Tasks
            </Button>
            {extractedTasks.length > 0 && (
              <div className="space-y-1">
                {extractedTasks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    <Zap className="w-3 h-3" /> {t.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="chat">
          <div className="bg-white border border-slate-200">
            <div className="p-4 min-h-[200px]">
              {response && (
                <div className="p-3 bg-violet-50 border border-violet-200 text-sm text-slate-700 whitespace-pre-wrap mb-4">
                  {response}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 p-3 border-t border-slate-200">
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleQuery()}
                placeholder="Ask Agent Mail anything about your emails..."
                className="h-9 text-sm rounded-none flex-1" />
              <Button onClick={handleQuery} disabled={loading || !query.trim()}
                className="h-9 w-9 p-0 rounded-none bg-violet-600 hover:bg-violet-500">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
